/**
 * TowerOverlay.tsx — Tower Exterior Treatment B (Implied Minimalist Surround)
 *
 * H3 locked: Option B — implied rails + SVG overlays (1hr build, NOT literal frame)
 *
 * W20 fix (2026-05-28): Added currentCp + cpState props for orb rendering.
 * Orb Y-position = floor index of currentCp (range 1-10 using CP_POSITIONS).
 * Clamp: if currentCp is null, 0, or out of range, orb is hidden entirely.
 * Orb color: canon 5-state palette by cpState.
 *
 * W25 fix (2026-05-27): Unfrozen. CP_NAMES now imported from helix-checkpoints.
 * W44.4 fix (2026-05-30): CP_POSITIONS recalibrated so CP1 = waterline (0.75) and
 * CP10 = roof crown (0.07). Prior values (0.91–0.09) placed CP1–2 below the image
 * waterline. Now matches Tower.tsx FLOOR_Y_PCT canonical mapping.
 *
 * Brand: No exclamation points. No emojis.
 */

import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";

// Canon 5-state orb colors (CANON_VISUALS_LOCKED.md, locked 2026-05-27)
const ORB_COLORS = {
  "not-started": "#6B7280",
  "failed":      "#DC2626",
  "at-risk":     "#EA580C",
  "in-progress": "#EAB308",
  "passed":      "#16A34A",
} as const;

export type CpState = keyof typeof ORB_COLORS;

interface Props {
  height: number;
  width: number;
  showFloorLabels?: boolean;
  /** Current checkpoint (1-10). Null/out-of-range = orb hidden. */
  currentCp?: number | null;
  /** CP state drives orb color. Defaults to "not-started" (gray). */
  cpState?: CpState | null;
}

// W66.X19c (2026-06-03): CP_POSITIONS recalibrated. Prior W66.X19b anchored CP1 at
// 58.76% (bottommost body ring above lobby). CP1 now sits on the lobby ground-floor
// reception. Tower spans 11.7% (top crown / rooftop) to 63.87% (lobby reception),
// with the 10 CPs evenly spaced. CP1 = lobby, CP10 = rooftop.
// W66.X19c (2026-06-03): CP1 anchored at lobby ground-floor reception (0.6387), CP10 at rooftop (0.117). Approved by Bryan K. Short after tower_orb_preview_v4.
const CP_POSITIONS = [0.6387, 0.5807, 0.5227, 0.4648, 0.4068, 0.3488, 0.2909, 0.2329, 0.1749, 0.117];

// CP names derived from canonical source of truth in helix-checkpoints.ts.
const CP_NAMES = HELIX_CHECKPOINTS.map((cp) => cp.name);

export default function TowerOverlay({
  height,
  width,
  showFloorLabels = true,
  currentCp = null,
  cpState = null,
}: Props) {
  const railInsetBottom = width * 0.12;
  const railInsetTop    = width * 0.14;
  const leftRailXBottom  = railInsetBottom;
  const leftRailXTop     = railInsetTop;
  const rightRailXBottom = width - railInsetBottom;
  const rightRailXTop    = width - railInsetTop;

  // Orb math: clamp currentCp to [1-10]; hide if null or out-of-range
  const cpIdx =
    currentCp != null && !isNaN(currentCp) && currentCp >= 1 && currentCp <= 10
      ? Math.round(currentCp) - 1
      : null;
  const showOrb   = cpIdx !== null;
  const orbYPct   = showOrb ? CP_POSITIONS[cpIdx as number] : 0;
  const orbY      = orbYPct * height;
  const orbColor  = ORB_COLORS[cpState ?? "not-started"];

  // Horizontally center orb between rails at its floor Y
  const orbT      = showOrb ? (orbY - height * 0.04) / (height * 0.92) : 0;
  const orbLeftX  = leftRailXTop  + (leftRailXBottom - leftRailXTop)  * orbT;
  const orbRightX = rightRailXTop + (rightRailXBottom - rightRailXTop) * orbT;
  const orbCX     = (orbLeftX + orbRightX) / 2;

  return (
    <svg
      style={{
        position: "absolute",
        top: 0, left: 0,
        width: "100%", height: "100%",
        pointerEvents: "none",
        zIndex: 6,
        overflow: "visible",
      }}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="railFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="rgba(255,255,255,0)" />
          <stop offset="8%"   stopColor="rgba(220,38,38,0.55)" />
          <stop offset="92%"  stopColor="rgba(220,38,38,0.55)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
        {showOrb && (
          <filter id="orbGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      {/* Cylinder body tint */}
      <polygon
        points={`${leftRailXTop},${height*0.04} ${rightRailXTop},${height*0.04} ${rightRailXBottom},${height*0.96} ${leftRailXBottom},${height*0.96}`}
        fill="rgba(220,38,38,0.025)" stroke="none"
      />

      {/* Left rail */}
      <line x1={leftRailXTop} y1={height*0.04} x2={leftRailXBottom} y2={height*0.96}
        stroke="url(#railFade)" strokeWidth={2} strokeLinecap="round" />
      {/* Right rail */}
      <line x1={rightRailXTop} y1={height*0.04} x2={rightRailXBottom} y2={height*0.96}
        stroke="url(#railFade)" strokeWidth={2} strokeLinecap="round" />

      {/* Top cap */}
      <ellipse cx={width/2} cy={height*0.04}
        rx={(rightRailXTop-leftRailXTop)/2} ry={7}
        fill="none" stroke="rgba(220,38,38,0.45)" strokeWidth={1.5} />
      {/* Bottom cap */}
      <ellipse cx={width/2} cy={height*0.96}
        rx={(rightRailXBottom-leftRailXBottom)/2} ry={9}
        fill="none" stroke="rgba(220,38,38,0.45)" strokeWidth={1.5} />

      {/* Floor rules + labels */}
      {CP_POSITIONS.map((yPct, i) => {
        const y = yPct * height;
        const cpNum = i + 1;
        const t = (y - height*0.04) / (height*0.92);
        const lx = leftRailXTop  + (leftRailXBottom - leftRailXTop)  * t;
        const rx = rightRailXTop + (rightRailXBottom - rightRailXTop) * t;
        return (
          <g key={cpNum}>
            <line x1={lx} y1={y} x2={rx} y2={y}
              stroke="rgba(255,255,255,0.18)" strokeWidth={1} strokeDasharray="4 8" />
            {showFloorLabels && (
              <text x={lx-8} y={y+4} textAnchor="end"
                fill="rgba(255,255,255,0.65)" fontSize={11}
                fontFamily="'Inter', sans-serif" fontWeight={700} letterSpacing="0.1em">
                {cpNum}
              </text>
            )}
            {showFloorLabels && (
              <text x={rx+8} y={y+4} textAnchor="start"
                fill="rgba(255,255,255,0.55)" fontSize={10}
                fontFamily="'Inter', sans-serif" fontWeight={500}>
                {CP_NAMES[i]}
              </text>
            )}
          </g>
        );
      })}

      {/* CLOSE label — CP10 = penthouse = top of tower */}
      <text x={width/2} y={height*0.025} textAnchor="middle"
        fill="rgba(22,163,74,0.85)" fontSize={11}
        fontFamily="'Inter', sans-serif" fontWeight={700} letterSpacing="0.25em">
        CLOSE
      </text>
      {/* DEAL ENTERS label — CP1 = ground floor = bottom of tower */}
      <text x={width/2} y={height*0.985} textAnchor="middle"
        fill="rgba(255,255,255,0.7)" fontSize={11}
        fontFamily="'Inter', sans-serif" fontWeight={700} letterSpacing="0.25em">
        DEAL ENTERS
      </text>

      {/* Orb at currentCp floor */}
      {showOrb && (
        <g>
          <circle cx={orbCX} cy={orbY} r={18}
            fill={`${orbColor}22`} stroke={`${orbColor}55`} strokeWidth={1} />
          <circle cx={orbCX} cy={orbY} r={12} fill={`${orbColor}33`} />
          <circle cx={orbCX} cy={orbY} r={8} fill={orbColor}
            filter="url(#orbGlow)" stroke="rgba(255,255,255,0.75)" strokeWidth={1.5} />
          <circle cx={orbCX-2.5} cy={orbY-2.5} r={2.2} fill="rgba(255,255,255,0.55)" />
          <text x={orbCX+14} y={orbY+4} textAnchor="start"
            fill="rgba(255,255,255,0.90)" fontSize={10}
            fontFamily="'Inter', sans-serif" fontWeight={700} letterSpacing="0.06em">
            {`CP${currentCp}`}
          </text>
        </g>
      )}
    </svg>
  );
}
